import React, { useEffect, useState, useMemo } from 'react';
import Axios from 'axios';

const Reports = () => {
  const [individualData, setIndividualData] = useState([]);
  const [yearlyData, setYearlyData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    
    // Fetch both datasets concurrently
    Promise.all([
      Axios.get(`http://localhost:3001/api/reports/individual/${userId}`),
      Axios.get(`http://localhost:3001/api/reports/yearly/${userId}`)
    ])
      .then(([resIndiv, resYearly]) => {
        setIndividualData(resIndiv.data || []);
        setYearlyData(resYearly.data || []);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching reports:", err);
        setLoading(false);
      });
  }, []);

  // --- CALCULATIONS FOR TOP METRICS (Individual Data) ---
  const totalInvoices = individualData.length;
  const totalDueSum = individualData.reduce((acc, row) => acc + (Number(row.duebal) || 0), 0);
  const totalDiscountSum = individualData.reduce((acc, row) => acc + (Number(row.totalDiscounts) || 0), 0);
  
  const totalVatSum = individualData.reduce((acc, row) => {
    const vatPercent = typeof row.vat === 'string' 
      ? Number(row.vat.replace(/[^0-9.]/g, '')) 
      : (Number(row.vat) || 0);
    return acc + ((Number(row.netRevenue || 0) - Number(row.duebal || 0)) * (vatPercent / 100));
  }, 0);
  
  const totalNetRevenueIncludingVat = individualData.reduce((acc, curr) => acc + Number(curr.netRevenue || 0), 0);
  const netRevenueExcludingVat = (totalNetRevenueIncludingVat - totalVatSum) - totalDueSum;

  const totalFiscalYears = useMemo(() => {
    if (individualData.length === 0) return 0;
    const dates = individualData.map(row => new Date(row.month).getTime()).filter(t => !isNaN(t));
    if (dates.length === 0) return 1;
    const minDate = new Date(Math.min(...dates));
    const maxDate = new Date(Math.max(...dates));

    let years = maxDate.getFullYear() - minDate.getFullYear();
    if (maxDate.getMonth() < minDate.getMonth() || 
       (maxDate.getMonth() === minDate.getMonth() && maxDate.getDate() < minDate.getDate())) {
      years--;
    }
    return Math.max(1, years + 1); 
  }, [individualData]);

  if (loading) return <div style={{ color: 'white', padding: '20px' }}>Loading Financial Data...</div>;

  return (
    <div style={containerStyle}>
      <h2 style={{ color: '#e9edef', marginBottom: '20px', borderBottom: '2px solid #00a884', paddingBottom: '10px' }}>
        Financial Overview Yearly Report
      </h2>

      {/* TOP METRICS CARDS */}
      <div style={statsGrid}>
        <div style={cardStyle}>
          <span style={labelStyle}>Total Invoices</span>
          <h2 style={valueStyle}>{totalInvoices}</h2>
        </div>
        <div style={cardStyle}>
          <span style={labelStyle}>Total Age of Business</span>
          <h2 style={valueStyle}>{totalFiscalYears} Year(s)</h2>
        </div>
        <div style={cardStyle}>
          <span style={labelStyle}>Total Discount Given</span>
          <h2 style={{ ...valueStyle, color: '#e74c3c' }}>TK {totalDiscountSum.toLocaleString()}/-</h2>
        </div>
        <div style={cardStyle}>
          <span style={labelStyle}>Total VAT Collected</span>
          <h2 style={{ ...valueStyle, color: '#ffd000' }}>TK {Math.round(totalVatSum).toLocaleString()}/-</h2>
        </div>
        <div style={cardStyle}>
          <span style={labelStyle}>Total Due Balance</span>
          <h2 style={{ ...valueStyle, color: '#ff1900' }}>
            TK {totalDueSum.toLocaleString()}/-
          </h2>
        </div>
        <div style={cardStyle}>
          <span style={labelStyle}>Total Net Revenue (Excl. VAT)</span>
          <h2 style={{ ...valueStyle, color: '#00a884' }}>TK {Math.round(netRevenueExcludingVat).toLocaleString()}/-</h2>
        </div>
      </div>

      {/* YEARLY BREAKDOWN TABLE */}
      <div style={tableWrapper}>
        <h3 style={{ padding: '15px', margin: 0, backgroundColor: '#011433' }}>Yearly Breakdown</h3>
        <table style={tableStyle}>
          <thead>
            <tr style={headerRowStyle}>
              <th>SL No.</th>
              <th>Year</th>
              <th>Invoices</th>
              <th>Gross Revenue</th>
              <th>Discount Amount</th>
              <th>Taxable Amount</th>
              <th>Value Added Tax (VAT)</th> {/* Restored column */}
              <th>Revenue (Incl. VAT)</th>
              <th>Amount Received</th>
              <th>Due Balance</th>
              <th>Net Revenue (Excl. VAT)</th>
            </tr>
          </thead>
          <tbody>
            {yearlyData.map((row, index) => {
              const gross = Number(row.ygrossRevenue) || 0;
              const discount = Number(row.ytotalDiscounts) || 0;
              const totdue = Number(row.yduebal) || 0;
              const totnetrev = Number(row.ynetRevenue) || 0;
              
              const taxableAmount = (gross - discount); 
              const NetReceivedAmount = totnetrev - totdue; 

              // COMBINE INDIVIDUAL MATH: Extract matching rows for this table row year string
              const itemsForThisYear = individualData.filter(item => {
                if (!item.month) return false;
                const itemYear = new Date(item.month).getFullYear().toString();
                return itemYear === row.yyear?.toString();
              });

              // Sum the actual calculated VAT across all individual entries within this year
              const tRevenuevat = itemsForThisYear.reduce((acc, item) => {
                const vatPercent = typeof item.vat === 'string' 
                  ? Number(item.vat.replace(/[^0-9.]/g, '')) 
                  : (Number(item.vat) || 0);
                const itemReceived = (Number(item.netRevenue) || 0) - (Number(item.duebal) || 0);
                return acc + (itemReceived * (vatPercent / 100));
              }, 0);

              // Calculate Net Profit Amount matching your metric matrix configurations
              const NetProfitAmount = NetReceivedAmount - tRevenuevat;

              return (
                <tr key={index} style={rowStyle}>
                  <td>{index + 1}</td>
                  <td>{row.yyear}</td>
                  <td>{row.yinvoiceCount}</td>
                  <td>TK {gross.toLocaleString()}</td>
                  <td style={{ color: '#e74c3c' }}>- TK {discount.toLocaleString()}</td>
                  <td>TK {taxableAmount.toLocaleString()}</td>
                  <td style={{ color: '#ffd000' }}>+ TK {Math.round(tRevenuevat).toLocaleString()}</td>
                  <td style={{ color: '#fff' }}>TK {totnetrev.toLocaleString()}</td>
                  <td style={{ fontWeight: 'bold', color: '#00a7fa' }}>TK {NetReceivedAmount.toLocaleString()}</td>
                  <td style={{ fontWeight: 'bold', color: totdue === 0 ? '#ffd001' : '#ff3300' }}>
                    {totdue === 0 ? "---" : `TK ${totdue.toLocaleString()}`}
                  </td> 
                  <td style={{ fontWeight: 'bold', color: '#00a884' }}>TK {Math.round(NetProfitAmount).toLocaleString()}</td> 
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// --- STYLES ---
const containerStyle = { padding: '10px', maxWidth: '1920px', margin: '0 auto' };
const statsGrid = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr', gap: '15px', marginBottom: '30px' }; // Adjusted for 6 cards scannability
const cardStyle = { backgroundColor: '#011433', padding: '15px', borderRadius: '12px', textAlign: 'center', boxShadow: '0 4px 6px rgba(0,0,0,0.3)' };
const labelStyle = { color: '#ffffff', fontSize: '12px', textTransform: 'uppercase' };
const valueStyle = { fontSize: '20px', margin: '10px 0 0 0', color: '#ffffff' };
const tableWrapper = { backgroundColor: '#011433', borderRadius: '12px', overflow: 'hidden', color: '#e9edef' };
const tableStyle = { width: '100%', borderCollapse: 'collapse' };
const headerRowStyle = { backgroundColor: '#000b1d', textAlign: 'left', color: '#8696a0' };
const rowStyle = { borderBottom: '1px solid #202c33', padding: '15px' };

export default Reports;
